import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

const TOUR_STEPS: TourStep[] = [
    {
        selector: '[data-tour="addon-service-info"]',
        title: 'Thông Tin Dịch Vụ Gốc',
        description:
            'Thông tin dịch vụ cha (parent service):\n• Tên dịch vụ\n• Nhóm sản phẩm\n• Khách hàng\n• Trạng thái dịch vụ',
    },
    {
        selector: '[data-tour="addon-sales-tracking"]',
        title: 'Sales Phụ Trách',
        description:
            'Chọn nhân viên Sales phụ trách addon này:\n• Có thể chọn nhiều người\n• Mặc định theo Sales của dịch vụ gốc',
    },
    {
        selector: '[data-tour="addon-product-config"]',
        title: 'Cấu Hình Addon',
        description:
            'Cấu hình dịch vụ addon:\n• Chọn sản phẩm addon phù hợp\n• Thiết lập thời gian sử dụng\n• Cấu hình giá và thanh toán',
    },
    {
        selector: '[data-tour="addon-actions"]',
        title: 'Tạo Dịch Vụ Addon',
        description:
            'Sau khi hoàn tất cấu hình:\n• Click "Tạo dịch vụ" để tạo addon\n• Hoặc "Quay lại" để hủy bỏ',
    },
]

/**
 * Tour hook for Create Addons page
 */
const useCreateAddonsTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            if (stepIndex < 0 || stepIndex >= steps.length) {
                endTour()
                return
            }
            showTourStep(stepIndex, steps)
        },
        [endTour]
    )

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            cleanupTourElements()
            setCurrentTourStep(stepIndex)

            const step = steps[stepIndex]
            if (!step) {
                endTour()
                return
            }

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    setTimeout(() => goToStep(stepIndex + 1, steps), 100)
                } else {
                    endTour()
                }
                return
            }

            createTourOverlay()

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour, goToStep]
    )

    const startTour = useCallback(() => {
        injectTourStyles()
        setIsActive(true)

        // Filter steps to only include those with visible elements
        const visibleSteps = TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })

        if (visibleSteps.length === 0) {
            console.warn('No tour elements found on this page')
            return
        }

        showTourStep(0, visibleSteps)
    }, [showTourStep])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useCreateAddonsTour
